import { useState } from 'react'
import { useAdminProjectComparisons, useDeleteComparison, useRegenerateComparison } from '@/hooks/useAdminComparisons'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Trash2, RefreshCw, GitCompare, Calendar, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface ProjectComparationsSectionProps {
  projectId: string
}

export function ProjectComparisonsSection({ projectId }: ProjectComparationsSectionProps) {
  const { data: comparisons, isLoading } = useAdminProjectComparisons(projectId)
  const deleteMutation = useDeleteComparison()
  const regenerateMutation = useRegenerateComparison()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [comparisonToDelete, setComparisonToDelete] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!comparisonToDelete) return

    try {
      await deleteMutation.mutateAsync(comparisonToDelete)
      toast.success('Comparison deleted successfully')
      setDeleteDialogOpen(false)
      setComparisonToDelete(null)
    } catch (error) {
      console.error('Failed to delete comparison:', error)
      toast.error('Failed to delete comparison')
    }
  }

  const handleRegenerate = async (comparisonId: string) => {
    try {
      await regenerateMutation.mutateAsync(comparisonId)
      toast.success('Comparison regenerated successfully')
    } catch (error) {
      console.error('Failed to regenerate comparison:', error)
      toast.error('Failed to regenerate comparison')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!comparisons || comparisons.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <GitCompare className="h-12 w-12 text-muted-foreground/50 mb-2" />
        <p className="text-sm text-muted-foreground">No comparisons found for this project</p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-2">
        {comparisons.map((comparison) => (
          <div
            key={comparison.id}
            className="flex items-center justify-between p-3 rounded-md border bg-card hover:bg-accent/50 transition-colors"
          >
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <GitCompare className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">
                  {comparison.from_commit.version} → {comparison.to_commit.version}
                </span>
                {!comparison.has_activity_details && (
                  <Badge variant="outline" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    No details
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(comparison.created_at)}
                </span>
                {comparison.has_activity_details && (
                  <span>Size: {formatBytes(comparison.activity_details_size_bytes)}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRegenerate(comparison.id)}
                disabled={regenerateMutation.isPending}
                title="Regenerate with current algorithm"
              >
                {regenerateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setComparisonToDelete(comparison.id)
                  setDeleteDialogOpen(true)
                }}
                disabled={deleteMutation.isPending}
                title="Delete comparison"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comparison?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the cached comparison results. The comparison will be regenerated with the current algorithm the next time it's requested.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
